/**
 * Tipos e helpers para o envelope de resposta padrão do backend.
 *
 * O backend Pulso encapsula todo endpoint de indicador em:
 *
 *   {
 *     data: <payload original>,
 *     metadata: {
 *       fonte: "SIH/DataSUS",
 *       ano_solicitado: 2025,
 *       ano_efetivo: 2025,
 *       completude_pct: 87,
 *       ufs_faltantes: [],
 *       meses_faltantes: ["set"],
 *       eh_estimativa: false,
 *       metodologia: null,
 *       ultima_atualizacao_etl: null
 *     },
 *     warnings: ["Dados 2025 parciais (87% completos)..."]
 *   }
 *
 * Use `isEnvelope()` pra detectar e `unwrapEnvelope()` pra extrair o payload.
 * Os utilitários `cachedJson` e `httpClient` já fazem unwrap automático.
 */

export interface EnvelopeMetadata {
  fonte: string
  ano_solicitado?: number | null
  ano_efetivo?: number | null
  completude_pct?: number | null
  ufs_faltantes?: string[]
  meses_faltantes?: string[]
  eh_estimativa?: boolean
  metodologia?: string | null
  ultima_atualizacao_etl?: string | null
  [extra: string]: unknown
}

export interface Envelope<T = unknown> {
  data: T
  metadata: EnvelopeMetadata
  warnings: string[]
}

/** Type guard — verdadeiro se a resposta segue o shape do envelope backend. */
export function isEnvelope(value: unknown): value is Envelope {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  if (!('data' in v) || !('metadata' in v) || !('warnings' in v)) return false
  const meta = v.metadata
  if (!meta || typeof meta !== 'object') return false
  // O campo `fonte` é mandatório no build_envelope do backend — usado como
  // discriminador pra evitar confundir com payloads que casualmente têm
  // `data`/`metadata` (ex.: respostas legadas da HttpClient ApiResponse).
  return 'fonte' in (meta as Record<string, unknown>)
}

/** Desempacota envelope; se não for envelope, retorna o valor original. */
export function unwrapEnvelope<T = unknown>(value: unknown): T {
  if (isEnvelope(value)) {
    return value.data as T
  }
  return value as T
}
